<template>
	<v-defaults-provider :defaults="overlayDefaults">
		<v-card class="fill-height">
			<Toolbar :panel-params="params" />
			<v-card class="pa-0 d-flex flex-column" style="height: calc(100% - 52px)">
				<Toolbar v-if="selectedStationID" :items="headerItems" />
				<FullCalendar
					v-if="selectedStationID"
					id="calendar"
					ref="fullCalendar"
					class="pt-4"
					:options="calendarOptions"
				>
					<template #eventContent="{event}">
						<div class="fc-event-main-frame">
							<div class="fc-event-time">{{ formatTime(event.start) }} - {{ formatTime(event.end) }}</div>
							<div class="fc-event-title-container">
								<div class="fc-event-title fc-sticky">
									<div>{{ event.title }}</div>
									<label class="text-caption text-body-2 px-1">
										{{ getStatusLabel(event) }}
									</label>
									<i class="mdi" :class="getStatusIcon(event)"></i>
								</div>
							</div>
						</div>
					</template>
				</FullCalendar>
				<v-overlay v-model="warningModalOpened" class="align-center" contained :close-on-content-click="false">
					<WarningDialog
						:priority-lane="pendingPriorityLane"
						:selected-station-i-d="selectedStationID"
						@change-priority-lane="changePriorityLane"
						@close="closeWarningModal()"
					/>
				</v-overlay>
				<CalendarEventTooltip :event="hoverEvent" :timezone="stationTimezone" />
				<NoStationSelectedContainer v-if="!selectedStation" />
				<Loader v-else :value="eventsLoading || priorityLanesLoading" />
			</v-card>
		</v-card>
	</v-defaults-provider>
</template>

<script>
/* Libraries */
import "@fullcalendar/core/vdom";
import {sortBy} from "lodash";
import emitter from "@/emitter";
import config from "@/config";
import moment from "moment-timezone";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";

/* Modules */
import {VBtn, VDatePicker, VIcon, VMenu, VSelect} from "vuetify/components";
import Toolbar from "@/components/Toolbar.vue";
import FullCalendar from "@fullcalendar/vue3";
import CalendarEventTooltip from "@/components/CalendarEventTooltip.vue";
import Loader from "@/components/Loader.vue";
import NoStationSelectedContainer from "@/components/NoStationSelectedContainer.vue";
import WarningDialog from "@/components/dialogs/CalendarWarningDialog.vue";

/* Enums */
import PriorityLanes from "@/enums/PriorityLanes";
import PlaylistTypes from "@/enums/PlaylistTypes";
import PlaylistStatus from "@/enums/PlaylistStatus";
import SequencerModes from "@/enums/SequencerModes";
import POITypes from "@/enums/POITypes";

/* Mixins */
import PanelPropertiesMixin from "@/mixins/PanelProperties";

const PlaylistFound = {
	NOT_FOUND: 0,
	FOUND: 1,
	MISSING: 2,
	PENDING: 3
};

const PANEL_MIN_WIDTH = 500;
const PANEL_MAX_WIDTH = 700;

export default {
	name: "Calendar",

	components: {
		FullCalendar,
		Loader,
		CalendarEventTooltip,
		NoStationSelectedContainer,
		Toolbar,
		WarningDialog
	},

	mixins: [PanelPropertiesMixin],

	props: ["params"],

	data() {
		return {
			eventsLoading: true,
			priorityLanesLoading: true,
			warningModalOpened: false,
			datePickerOpened: false,
			priorityLanes: [],
			selectedLane: null,
			pendingPriorityLane: null,
			currentPlaylist: null,
			currentEvents: [],
			errorLaneMsg: null,
			selectedDate: moment.tz(this.stationTimezone).startOf("d"),
			projectedEvents: [],
			hoverEvent: {},
			calendarOptions: {
				schedulerLicenseKey: config.SCHEDULER_LICENSE_KEY,
				plugins: [resourceTimeGridPlugin, momentTimezonePlugin],
				headerToolbar: false,
				initialView: "resourceTimeGridDay",
				editable: false,
				selectable: false,
				selectAllow: false,
				allDaySlot: false,
				nowIndicator: true,
				slotEventOverlap: false, //https://fullcalendar.io/docs/slotEventOverlap
				slotDuration: "00:15:00", //https://fullcalendar.io/docs/slotDuration
				slotLabelInterval: {
					//https://fullcalendar.io/docs/slotLabelInterval
					hours: 1
				},
				timeZone: this.stationTimezone,
				resources: [
					{
						id: 1,
						title: "Priority Lane 1"
					},
					{
						id: 2,
						title: "Priority Lane 2"
					},
					{
						id: 3,
						title: "Scheduled Program"
					}
				],
				resourceOrder: "-id", //desc order by id
				height: "100%",
				events: (info, success) => {
					const period = {
						from: info.start.valueOf(),
						to: info.end.valueOf()
					};
					this.getProjectedEvents(period, success);
				},
				eventDataTransform: this.formatEventData,
				eventClick: ({event}) => {
					this.calendarEventClick(event);
				},
				loading: this.isEventsLoaded,
				eventMouseEnter: ({el, event}) => {
					this.hoverEvent = {
						el: el,
						title: event.title,
						start: event.start,
						end: event.end,
						duration: event.extendedProps.duration,
						priority: event.extendedProps.priority,
						statusLabel: this.getStatusLabel(event),
						statusIcon: this.getStatusIcon(event)
					};
				},
				eventDidMount: ({el, event}) => {
					el.style.background = this.getBgColors(event);

					// In case there is an updatedEventToReload, click on the event to reload the playlist
					if (
						event.extendedProps.id === this.updatedEventToReload?.id &&
						event.extendedProps.override_id === this.updatedEventToReload?.override_id
					) {
						this.calendarEventClick(event);
						this.updatedEventToReload = null;
					}
				}
			}
		};
	},

	computed: {
		headerItems() {
			return [
				{
					component: "div",
					props: {
						class: "text-body-2 mr-2"
					},
					slots: [
						{
							name: "default",
							items: [
								{
									component: "div",
									text: "Currently Playing"
								}
							]
						}
					]
				},
				{
					component: VIcon,
					props: {
						icon: "mdi-play-circle",
						class: "mr-2",
						size: 16,
						color: "#17BC85"
					}
				},
				{
					component: VSelect,
					props: {
						modelValue: this.selectedLane,
						class: "mr-2",
						items: this.priorityLanes,
						itemTitle: "name",
						itemValue: "priority",
						variant: "outlined",
						density: "compact",
						label: this.errorLaneMsg,
						singleLine: true,
						hideDetails: true,
						loading:
							this.eventsLoading ||
							this.priorityLanesLoading ||
							!(this.selectedLane || this.errorLaneMsg) ||
							!!this.pendingPriorityLane,
						disabled:
							!this.sequencerShowEpisodeID ||
							this.priorityLanesLoading ||
							!this.selectedLane ||
							!!this.errorLaneMsg ||
							!!this.pendingPriorityLane,
						width: "250px"
					},
					actions: {
						"update:model-value": data => this.showWarningModal(data)
					}
				},
				{
					component: VMenu,
					props: {
						modelValue: this.datePickerOpened,
						closeOnContentClick: false
					},
					slots: [
						{
							name: "activator",
							items: data => {
								return [
									{
										component: VBtn,
										props: {
											...data.props,
											class: "flex-grow-1",
											appendIcon: "mdi-calendar",
											text: this.selectedDate.format("dddd, MMMM D, YYYY"),
											variant: "text",
											elevation: 1
										},
										actions: {
											click: () => (this.datePickerOpened = !this.datePickerOpened)
										}
									}
								];
							}
						},
						{
							name: "default",
							items: [
								{
									component: VDatePicker,
									props: {
										modelValue: this.selectedDate,
										color: "secondary",
										hideHeader: true,
										showAdjacentMonths: true
									},
									actions: {
										"update:model-value": this.goToDate
									}
								}
							]
						}
					]
				},
				{
					component: VBtn,
					props: {
						icon: "mdi-calendar-refresh",
						variant: "text",
						density: "comfortable"
					},
					actions: {
						click: this.refreshEvents
					}
				},
				{
					component: VBtn,
					props: {
						icon: "mdi-home",
						variant: "text",
						density: "comfortable"
					},
					actions: {
						click: this.goToCurrentDate
					}
				}
			];
		},

		sequencerShowEpisodeID() {
			return this.$store.getters.getSequencerShowEpisodeID(this.selectedStationID);
		},

		sequencerStartStopPlaylistStatus() {
			return this.$store.getters.sequencerStartStopPlaylistStatus(this.selectedStationID);
		},

		sequencerModeStatus() {
			return this.$store.getters.sequencerModePending(this.selectedStationID);
		},

		sequencerActionStatus() {
			return this.$store.getters.sequencerActionPending(this.selectedStationID);
		},

		syncActive() {
			return this.$store.getters.getSyncToSelectionActive();
		}
	},

	watch: {
		selectedStationID: {
			handler(stationID, previousStationID) {
				if (stationID && stationID !== previousStationID) {
					if (previousStationID) {
						this.$store.commit("clearSchedulerEventsAndSubscriptions", previousStationID);
					}
					this.$nextTick(() => {
						this.$store.commit("incrementSchedulerUsageCounterMap", stationID);
						this.calendarAPI = this.$refs.fullCalendar.getApi();

						this.priorityLanesLoading = true;
						this.calendarAPI.refetchEvents();

						const period = {
							from: this.stationDateTime.clone().startOf("day").valueOf(),
							to: this.stationDateTime.clone().add(1, "day").startOf("day").valueOf()
						};
						this.getProjectedEvents(period, this.setAvailableLanes);

						this.$store.dispatch("getSchedulerSubscriptions", stationID);
					});
				}
			},
			immediate: true
		},

		sequencerShowEpisodeID: {
			handler(showEpisodeID, previousShowEpisodeId) {
				if (!showEpisodeID) {
					this.currentPlaylist = null;
					this.errorLaneMsg = "No Episode Playing";
				}

				if (showEpisodeID !== previousShowEpisodeId) {
					if (showEpisodeID) {
						this.selectedLane = null;
						this.errorLaneMsg = null;
						this.$store
							.dispatch("getPlaylistWithAssetsByShowEpisodeID", {
								stationID: this.selectedStationID,
								showEpisodeID,
								recalculate: false
							})
							.then(playlists => {
								this.currentPlaylist = playlists[0];

								if (this.currentPlaylist) {
									if (this.currentPlaylist.PlaylistType === PlaylistTypes.Schedule) {
										this.selectedLane = PriorityLanes.Scheduled.priority;
									} else if (this.currentPlaylist.PlaylistType === PlaylistTypes.SpecialShow) {
										this.selectedLane = this.currentPlaylist.Priority;
									}
								} else {
									this.errorLaneMsg = "No Episode Found";
								}
							})
							.catch(error => {
								console.log(error);
								this.currentPlaylist = null;
								this.errorLaneMsg = "Fail to load current episode";
							});
					}

					if (this.calendarAPI) {
						this.calendarAPI.refetchEvents();
					}
				}
			},
			immediate: true // force eager callback execution
		},

		stationDateTime(current, previous) {
			if (!current.isSame(previous, "minutes")) {
				//do it every minute
				const period = {
					from: current.clone().startOf("day").valueOf(),
					to: current.clone().add(1, "day").startOf("day").valueOf()
				};
				this.getProjectedEvents(period, this.setAvailableLanes);
			}
		},

		sequencerStartStopPlaylistStatus(current, previous) {
			if (!current.pending && previous.pending && this.pendingPriorityLane) {
				this.closeWarningModal(current.statusCode === 200, current.message);
			}
		},

		sequencerModeStatus(status) {
			// after switching to Manual mode, send a Stop action request
			if (this.syncToSelectionBeforeChangingLaneIsRequired && !status.pending) {
				this.$store.dispatch("sequencerActionRequest", {action: "Stop", msg: {EventID: null}});
			}
		},

		sequencerActionStatus(current, previous) {
			// after stopping the playlist, send the Sync to selection request
			if (this.syncToSelectionBeforeChangingLaneIsRequired && !current.pending && previous.pending) {
				this.syncToSelection();
			}
		},

		syncActive(active) {
			// after syncing to the selected event, close the warning modal
			if (this.syncToSelectionBeforeChangingLaneIsRequired && !active) {
				this.closeWarningModal(true);
				this.syncToSelectionBeforeChangingLaneIsRequired = false;
			}
		}
	},

	created() {
		emitter.on("calendar:playlistLoaded", this.playlistLoaded);
		emitter.on("calendar:refreshCalendarAndReloadEvent", updatedEvent => {
			this.refreshEvents();
			if (!updatedEvent.cancelled) {
				this.updatedEventToReload = updatedEvent;
			}
		});
	},

	mounted() {
		// Constraints belong to the group, so reapply them whenever the panel is moved into another one.
		this.updateMinWidth();
		this.groupChangeDisposable = this.params?.api?.onDidGroupChange(this.updateMinWidth);
	},

	beforeUnmount() {
		this.groupChangeDisposable?.dispose();

		emitter.off("calendar:playlistLoaded", this.playlistLoaded);
		emitter.off("calendar:refreshCalendarAndReloadEvent");
	},

	methods: {
		formatTime(date) {
			return moment.tz(date, this.stationTimezone).format("H:mm");
		},

		getStatusLabel(event) {
			if (event.extendedProps.status === "COMPLETED") {
				if (event.extendedProps.playlistFound === PlaylistFound.MISSING) {
					return "SYNC ERROR";
				}
				return "SYNCED";
			}
			return "NOT SYNCED";
		},

		getStatusIcon(event) {
			if (!event.extendedProps.id || event.extendedProps.playlistFound === PlaylistFound.PENDING) {
				return "";
			}

			if (event.extendedProps.status !== "COMPLETED") {
				if (event.extendedProps.playlistFound !== PlaylistFound.NOT_FOUND) {
					return "mdi-shield-alert-outline text-yellow";
				}
				return "";
			}

			const statusEnum = {
				0: "mdi-shield-alert-outline text-red",
				1: "mdi-shield-check-outline",
				2: "mdi-shield-check-outline text-red"
			};

			return statusEnum[event.extendedProps.playlistFound];
		},

		showWarningModal(lane) {
			this.pendingPriorityLane = lane;
			this.warningModalOpened = true;
		},

		async changePriorityLane(sequencerModeIsAuto) {
			if (this.pendingPriorityLane !== PriorityLanes.Scheduled.priority) {
				const eventToPlay = this.currentEvents.find(e => e.priority === this.pendingPriorityLane);
				this.pendingPriorityLanePlaylist = this.calendarAPI
					.getEvents()
					.find(e => e.extendedProps.id === eventToPlay.id)?.extendedProps?.playlist;

				if (!this.pendingPriorityLanePlaylist) {
					this.pendingPriorityLanePlaylist = await this.$store
						.dispatch("getPlaylistByShowEpisodeID", {
							stationID: this.selectedStationID,
							showEpisodeID: eventToPlay.id
						})
						.catch(error => {
							console.log("Fail to load Playlist by ShowEpisodeID", error);
							this.closeWarningModal(false, "Failed to retrieve the Playlist by ShowEpisodeID.");
						});
				}

				if (this.pendingPriorityLanePlaylist) {
					// In the case of a playlist with status Played/ Not Played we need to trigger sync to selection.
					// If the sequencer mode is set to Auto, we need to set it to Manual first, stop the playlist, sync
					// to selection and then switch back to Auto mode again
					if (
						[PlaylistStatus.Played, PlaylistStatus.NotPlayed].includes(
							this.pendingPriorityLanePlaylist.CustomPlaylist?.StatusCode
						)
					) {
						this.syncToSelectionBeforeChangingLaneIsRequired = true;
						if (sequencerModeIsAuto) {
							this.$store.dispatch("sequencerModeRequest", {
								stationID: this.selectedStationID,
								mode: SequencerModes.Manual
							});
							this.$store.commit("setSwitchToAutoMode", {
								stationID: this.selectedStationID,
								switchToAutoMode: true
							});
						} else {
							await this.syncToSelection();
						}
					} else {
						this.$store.dispatch("SequencerStartCustomPlaylist", {
							stationID: this.selectedStationID,
							PlaylistID: this.pendingPriorityLanePlaylist.PlaylistID
						});
					}
				} else {
					this.closeWarningModal(
						false,
						`The Show Episode ${eventToPlay.name} doesn't have a valid playlist. It won't be possible to switch lanes.`
					);
				}
			} else {
				this.$store.dispatch("SequencerStopCustomPlaylist", {
					stationID: this.selectedStationID,
					PlaylistID: this.currentPlaylist.PlaylistID
				});
			}
		},

		async syncToSelection() {
			const duration = moment(this.pendingPriorityLanePlaylist.ScheduledEndDateTime).diff(
				this.pendingPriorityLanePlaylist.ScheduledStartDateTime,
				"seconds"
			);

			const playlistShowEpisodeEvent = this.pendingPriorityLanePlaylist.Events.find(e => !!e.AssetID);
			const asset = await this.$store.dispatch("getAsset", playlistShowEpisodeEvent.AssetID);

			const startLiveShowPOI =
				this.pendingPriorityLanePlaylist.OverridePOIMarkup?.POIMarkers.find(
					m => m.POIType === POITypes.StartLiveShow
				)?.Position ??
				asset.POIMarkup.POIMarkers?.find(m => m.POIType === POITypes.StartLiveShow)?.Position ??
				0;
			const fadeOutPOI =
				this.pendingPriorityLanePlaylist.OverridePOIMarkup?.POIMarkers.find(m => m.POIType === POITypes.FadeOut)
					?.Position ?? duration;

			const OriginalOverridePOIStart = Math.min(fadeOutPOI - duration, 0);

			const playlistStartDateTime = moment(
				this.pendingPriorityLanePlaylist.ActualStartDateTime ??
					this.pendingPriorityLanePlaylist.ScheduledStartDateTime
			);

			let syncPosition = moment().diff(playlistStartDateTime, "seconds"); // elapsing time

			// We need to make sure that the position is not in the future of a live asset and
			// also not very close to live
			const assetReleaseDateTime = moment(asset.ReleaseDateTime);
			if (assetReleaseDateTime.clone().add(syncPosition, "seconds").isSameOrAfter(moment().add(6, "seconds"))) {
				syncPosition = moment().diff(assetReleaseDateTime, "seconds") - 6;
			}

			if (syncPosition < 0) {
				syncPosition = 0;
			}

			const msg = {
				RestartPosition: OriginalOverridePOIStart + syncPosition + startLiveShowPOI
			};

			this.$store.dispatch("sequencerSyncActionRequest", {
				action: `SyncToEvent.${playlistShowEpisodeEvent.EventID}`,
				msg,
				stationID: this.selectedStationID
			});
		},

		closeWarningModal(success, errorMsg) {
			if (success) {
				this.selectedLane = this.pendingPriorityLane;
				this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
					message: `Priority lane change requested: ${PriorityLanes.getNameByPriority(this.selectedLane)}`
				});
			} else {
				if (errorMsg) {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {message: errorMsg});
				}
			}

			this.pendingPriorityLane = null;
			this.warningModalOpened = false;
		},

		goToDate(date) {
			const dateFormatted = date.format("YYYY-MM-DD");
			this.datePickerOpened = false;
			this.selectedDate = moment.tz(dateFormatted, this.stationTimezone);
			this.calendarAPI.gotoDate(dateFormatted);
			this.calendarAPI.refetchEvents();
		},

		goToCurrentDate() {
			if (this.selectedDate.isSame(this.stationDateTime, "date")) {
				this.calendarAPI.refetchEvents();
			} else {
				this.selectedDate = this.stationDateTime;
				this.calendarAPI.today();
			}
			this.calendarAPI.scrollToTime(
				moment(this.stationDateTime).subtract(1, "h").startOf("h").format("HH:mm:ss")
			);
		},

		refreshEvents() {
			this.calendarAPI.refetchEvents();
		},

		getProjectedEvents(period, callback) {
			if (this.selectedStationID) {
				this.$store
					.dispatch("getStationSchedulerEvents", {stationID: this.selectedStationID, period, useCache: false})
					.then(events => {
						if (this.selectedDate.isSame(this.stationDateTime, "day") && this.priorityLanesLoading) {
							this.setAvailableLanes(events);
							this.priorityLanesLoading = false;
						}
						callback(events.filter(e => !e.cancelled && e.priority !== PriorityLanes.Split.priority)); // not Split show events
					});
			} else {
				callback([]);
			}
		},

		getEventPlaylists() {
			const from = this.selectedDate;
			const to = this.selectedDate.clone().endOf("day");
			const params = {ReturnChildEvents: true};

			this.$store
				.dispatch("getPlaylistBySchedule", {stationID: this.selectedStationID, from, to, params})
				.then(response => {
					const playlists = response.data;

					this.calendarAPI.getEvents().forEach(event => {
						const extendedProps = event.extendedProps;
						if (extendedProps.id) {
							const episodeID = extendedProps.override_id ?? extendedProps.id;
							const slotID = extendedProps.slot_override_id ?? extendedProps.slot_id;
							const showEpisodeID = `${episodeID}_${slotID}`;

							const playlistFound = playlists.filter(p => p.ShowEpisodeID === showEpisodeID);

							if (playlistFound.length > 0) {
								event.setExtendedProp("playlist", playlistFound[0]);

								const eventHours = moment.duration(event.extendedProps.duration, "s").hours();
								event.setExtendedProp(
									"playlistFound",
									eventHours === playlistFound.length ? PlaylistFound.FOUND : PlaylistFound.MISSING
								);
							} else {
								this.$store
									.dispatch("getPlaylistByShowEpisodeID", {
										stationID: this.selectedStationID,
										showEpisodeID
									})
									.then(playlist => {
										event.setExtendedProp("playlist", playlist);
										event.setExtendedProp(
											"playlistFound",
											playlist ? PlaylistFound.FOUND : PlaylistFound.NOT_FOUND
										);
									});
							}
						}
					});
				});
		},

		formatEventData(event) {
			const episodeID = event.override_id ?? event.id;
			const slotID = event.slot_override_id ?? event.slot_id;
			const showEpisodeID = `${episodeID}_${slotID}`;

			return {
				id: showEpisodeID,
				resourceId: event.priority,
				title: event.name,
				start: event.start,
				end: event.start + event.duration * 1000,
				extendedProps: {...event, playlistFound: PlaylistFound.PENDING}
			};
		},

		getBgColors(event) {
			const colors = {
				live: "rgb(242, 72, 34)",
				priority: "rgb(23, 188, 133)",
				overlap: "rgb(35, 169, 249)"
			};

			if (event.id === this.sequencerShowEpisodeID) {
				return colors.live;
			}

			if (event.extendedProps.priority === PriorityLanes.Lane1.priority) {
				return colors.priority;
			} else {
				const overlapEvents = this.findOverlapEvents(event);

				if (overlapEvents.length) {
					const overlapRanges = this.findOverlapRanges(event, sortBy(overlapEvents, "start"));
					const backgroundStops = this.findBackgroundStops(overlapRanges, colors);

					return `linear-gradient(${backgroundStops.join()})`;
				}

				return colors.priority;
			}
		},

		findOverlapEvents(event) {
			return this.calendarAPI.getEvents().filter(e => {
				return (
					e.extendedProps.priority < event.extendedProps.priority && // Higher Priority Events
					(moment(e.start).isBetween(event.start, event.end) ||
						moment(e.end).isBetween(event.start, event.end) ||
						(moment(e.start).isSameOrBefore(event.start) && moment(e.end).isSameOrAfter(event.end)))
				);
			});
		},

		findOverlapRanges(event, overlapEvents) {
			const overlapRanges = [];
			const duration = event.extendedProps.duration * 1000;

			for (const overlapEvent of overlapEvents) {
				const overlapStart = moment.max(moment(event.start), moment(overlapEvent.start));
				const overlapEnd = moment.min(moment(event.end), moment(overlapEvent.end));

				overlapRanges.push({
					start: (overlapStart.diff(moment(event.start)) / duration) * 100,
					end: (overlapEnd.diff(moment(event.start)) / duration) * 100
				});
			}

			return overlapRanges;
		},

		findBackgroundStops(overlapRanges, colors) {
			const backgroundStops = [];
			for (let i = 0; i < overlapRanges.length; i++) {
				if (i === 0 && overlapRanges[i].start > 0) {
					backgroundStops.push(`${colors.priority} 0% ${overlapRanges[i].start}%`);
				}

				if (overlapRanges[i - 1] && overlapRanges[i - 1].end < overlapRanges[i].start) {
					backgroundStops.push(`${colors.priority} ${overlapRanges[i - 1].end}% ${overlapRanges[i].start}%`);
				}

				let j = i;
				for (j; j < overlapRanges.length; j++) {
					if (overlapRanges[j + 1] && overlapRanges[j + 1].start <= overlapRanges[j].end) {
						continue;
					}
					break;
				}
				backgroundStops.push(`${colors.overlap} ${overlapRanges[i].start}% ${overlapRanges[j].end}%`);
				i = j;

				if (i === overlapRanges.length - 1 && overlapRanges[i].end < 100) {
					backgroundStops.push(`${colors.priority} ${overlapRanges[i].end}% 100%`);
				}
			}

			return backgroundStops;
		},

		isEventsLoaded(isLoading) {
			this.eventsLoading = isLoading;
			if (!isLoading) {
				if (this.selectedDate.isSame(this.stationDateTime, "day")) {
					this.calendarAPI.scrollToTime(
						moment(this.stationDateTime).subtract(1, "h").startOf("h").format("HH:mm:ss")
					);
				} else {
					this.calendarAPI.scrollToTime("00:00:00");
				}
				this.getEventPlaylists();
			}
		},

		setAvailableLanes(events) {
			this.currentEvents = events.filter(e =>
				this.stationDateTime.isBetween(e.start, e.start + e.duration * 1000, undefined, "[]")
			);

			// Disable the lanes that are not match to any current event
			this.priorityLanes = PriorityLanes.get()
				.filter(lane => lane.priority !== PriorityLanes.Split.priority)
				.map(lane => ({
					...lane,
					props: {
						disabled: !this.currentEvents.some(e => e.priority === lane.priority)
					}
				}));
		},

		calendarEventClick(event) {
			const eventObj = event.extendedProps;
			const eventToEmit = {
				...eventObj,
				title: eventObj.name,
				resourceTitle: PriorityLanes.getNameByPriority(eventObj.priority),
				ShowEpisodeID: event.id,
				isAboutToStart: this.eventIsAboutToStart(event.extendedProps)
			};

			emitter.emit("calendar:loadPlaylist", eventToEmit);
		},

		eventIsAboutToStart(event) {
			const now = moment();
			if (now.isBefore(event.start) && moment(event.start).diff(now, "h") === 0) {
				// It will start in the next hour
				return this.calendarAPI.getEvents().every(e => {
					return !(
						e.extendedProps.priority !== PriorityLanes.Scheduled && // Priority 1 or 2. Event comes from a Special Show
						moment(e.extendedProps.start).isBetween(now, event.start) // Event will start first
					);
				});
			}
			return false;
		},

		playlistLoaded(showEpisodeID) {
			this.calendarAPI.getEvents().forEach(e => {
				if (e.id !== showEpisodeID) {
					e.setProp("classNames", []);
				} else {
					e.setProp("classNames", ["selected"]);
				}
			});
		},

		updateMinWidth() {
			this.params?.api?.group?.api?.setConstraints({
				minimumWidth: PANEL_MIN_WIDTH,
				maximumWidth: PANEL_MAX_WIDTH
			});
		}
	}
};
</script>

<style scoped lang="scss">
.v-divider {
	border-color: black;
	opacity: 1;
}

.v-select__slot {
	color: $grey-600;
}

.v-select__selections {
	width: 160px;
	justify-content: start;

	&:deep(.v-select__selection) {
		color: transparent !important;
	}
}

:deep(#calendar) {
	thead .fc-scroller {
		overflow: hidden !important;
	}

	.fc-col-header {
		font-size: 10px;
		overflow: hidden;
	}

	.fc-day-today {
		background: transparent !important;
	}

	.fc-event {
		border-color: transparent;
		border-radius: 2px;
		margin-left: 1px;

		.fc-event-selected:after,
		&:focus:after {
			background: unset;
		}

		.fc-event-main {
			padding: 0;

			.fc-event-time {
				font-weight: 700;
				text-align: center;
			}

			.fc-event-title-container {
				background-color: rgba(255, 255, 255, 0.75);

				.fc-event-title {
					color: $grey-600;
					text-align: center;
				}
			}
		}

		&.selected {
			box-shadow: rgba(0, 0, 0, 0.5) 0 0 30px;

			.fc-event-main {
				background: rgba(0, 0, 0, 0.25) !important;
			}
		}
	}
}
</style>
